/** Ephemeral agent-status row (typing / tool-call / handoff), rendered between the message list and the input bar so it
 *  stays visible at any scroll position. Driven by `MessagingState.agentStatus` -- it does NOT subscribe to the WS. */

import { useInjectStyles } from '@federa/components/hooks/useInjectStyles';
import { colors } from '@federa/components/css-refs.colors';
import { spacing } from '@federa/components/css-refs.spacing';
import { typography } from '@federa/components/css-refs.typography';
import { borderRadius } from '@federa/components/css-refs.effects';
import { ARIA_ROLES, ARIA_LIVE } from '@federa/components/common/aria-tokens';
import { FLEX_ROW_CENTER_GAP2_STYLE } from '@federa/components/common/style-primitives';
import type { AgentStatusFrame } from '@federa/types/conversations-chat-structs';

const AGENT_STATUS_KEYFRAMES_ID = 'agent-status-keyframes';

/** Blink cycle for the three status dots. Deliberately off the DS duration scale, which stops at 800ms: this is an idle
 *  "still working" hint, and a pulse that fast reads as urgency. */
const DOT_CYCLE_MS = 1200;
/** Each dot starts an eighth of a cycle after the one before it, so the three read left-to-right as one wave. DERIVED
 *  from the cycle rather than restated, because the offsets are a ratio of it -- pinned literals desync the wave the
 *  moment the cycle moves. */
const DOT_STAGGER_MS = DOT_CYCLE_MS / 8;

const AGENT_STATUS_KEYFRAMES = `
.agent-status-dot {
	display: inline-block;
	width: ${spacing[1]};
	height: ${spacing[1]};
	border-radius: 50%;
	background-color: ${colors.text.secondary};
	animation: agent-status-blink ${DOT_CYCLE_MS}ms infinite ease-in-out;
}
.agent-status-dot:nth-child(2) { animation-delay: ${DOT_STAGGER_MS}ms; }
.agent-status-dot:nth-child(3) { animation-delay: ${DOT_STAGGER_MS * 2}ms; }
@keyframes agent-status-blink {
	0%, 80%, 100% { opacity: 0.2; }
	40% { opacity: 1; }
}
`;

const ROW_STYLE: React.CSSProperties = {
	...FLEX_ROW_CENTER_GAP2_STYLE,
	padding: `${spacing[2]} ${spacing[3]}`,
	margin: `${spacing[2]} 0`,
	backgroundColor: colors.background.muted,
	borderRadius: borderRadius.lg,
	color: colors.text.secondary,
	fontSize: typography.fontSize.sm,
	fontStyle: 'italic',
};

const DOTS_CONTAINER: React.CSSProperties = {
	display: 'inline-flex',
	gap: spacing[1],
};

export function AgentStatusRow({ frame }: { frame: AgentStatusFrame }) {
	useInjectStyles(AGENT_STATUS_KEYFRAMES_ID, AGENT_STATUS_KEYFRAMES);
	return (
		<div role={ARIA_ROLES.status} aria-live={ARIA_LIVE.polite} style={ROW_STYLE}>
			<span style={DOTS_CONTAINER} aria-hidden="true">
				<span className="agent-status-dot" />
				<span className="agent-status-dot" />
				<span className="agent-status-dot" />
			</span>
			<span>{frame.label}</span>
		</div>
	);
}
